"use client"

import * as React from "react"
import Link from "next/link"
import { Calendar, MapPin, Users, Clock, DollarSign, Mic, MicOff, Share2, Info, UserPlus } from "lucide-react"
import { Button } from "@/components/ui/button"
import { ShareModal } from "./share-modal"
import type { League } from "@/lib/leagues-data"

interface LeagueListItemProps {
  league: League
}

export function LeagueListItem({ league }: LeagueListItemProps) {
  const [shareOpen, setShareOpen] = React.useState(false)
  
  const almostFull = league.spotsLeft <= 4
  const spotsPercentage = ((league.totalSpots - league.spotsLeft) / league.totalSpots) * 100

  return (
    <>
      <div className="bg-card border border-border rounded-lg hover:border-primary/30 transition-all duration-200 overflow-hidden group">
        <div className="flex flex-col lg:flex-row">
          {/* Game Badge - Left side accent */}
          <div className="lg:w-48 bg-secondary/50 p-4 lg:p-6 flex flex-col justify-center border-b lg:border-b-0 lg:border-r border-border">
            <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
              {league.game === "Sports Night" || league.game === "FPS Sundays" ? "Multi-Game" : "Game"}
            </span>
            <span className="text-lg font-bold text-foreground mt-1">{league.game}</span>
            {almostFull && (
              <span className="inline-flex items-center gap-1 text-xs bg-accent/20 text-accent px-2 py-0.5 rounded-full mt-2 w-fit">
                <span className="w-1.5 h-1.5 bg-accent rounded-full animate-pulse" />
                Almost Full
              </span>
            )}
          </div>

          {/* Main Content */}
          <div className="flex-1 p-4 lg:p-6">
            <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
              {/* League Details */}
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <h3 className="text-xl font-bold text-foreground group-hover:text-primary transition-colors">
                      {league.name}
                    </h3>
                    <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                      {league.description}
                    </p>
                  </div>
                </div>

                {/* Info Grid */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-6 gap-y-3 mt-4">
                  <div className="flex items-center gap-2 text-sm">
                    <Calendar className="h-4 w-4 text-primary shrink-0" />
                    <span className="text-foreground">
                      {league.day}s @ {league.time} {league.timezone}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <MapPin className="h-4 w-4 text-primary shrink-0" />
                    <span className="text-foreground">{league.region}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <Clock className="h-4 w-4 text-primary shrink-0" />
                    <span className="text-foreground">{league.weeksDuration} weeks</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <DollarSign className="h-4 w-4 text-primary shrink-0" />
                    <span className="text-foreground">${league.entryFee}/season</span>
                  </div>
                </div>

                {/* Tags & Additional Info */}
                <div className="flex flex-wrap items-center gap-2 mt-4">
                  <span className="inline-flex items-center gap-1 text-xs bg-primary/10 text-primary px-2 py-1 rounded">
                    {league.skillLevel}
                  </span>
                  {league.voiceRequired ? (
                    <span className="inline-flex items-center gap-1 text-xs bg-secondary text-muted-foreground px-2 py-1 rounded">
                      <Mic className="h-3 w-3" />
                      Voice Required
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-xs bg-secondary text-muted-foreground px-2 py-1 rounded">
                      <MicOff className="h-3 w-3" />
                      Voice Optional
                    </span>
                  )}
                  {league.tags.filter(t => t !== league.skillLevel && !t.includes("Voice")).map(tag => (
                    <span 
                      key={tag}
                      className="text-xs bg-secondary text-muted-foreground px-2 py-1 rounded"
                    >
                      {tag}
                    </span>
                  ))}
                </div>

                {/* Spots Progress */}
                <div className="mt-4">
                  <div className="flex items-center justify-between text-sm mb-1.5">
                    <span className="text-muted-foreground flex items-center gap-1.5">
                      <Users className="h-4 w-4" />
                      {league.spotsLeft} of {league.totalSpots} spots remaining
                    </span>
                    <span className="text-muted-foreground">
                      Starts {league.startsIn}
                    </span>
                  </div>
                  <div className="h-1.5 bg-secondary rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-primary rounded-full transition-all duration-500"
                      style={{ width: `${spotsPercentage}%` }}
                    />
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-row lg:flex-col gap-2 lg:gap-3 lg:ml-6 shrink-0">
                <Button
                  asChild
                  variant="outline"
                  className="flex-1 lg:flex-none lg:w-32 border-border hover:border-primary/50 gap-2"
                >
                  <Link href={`/leagues/${league.id}`}>
                    <Info className="h-4 w-4" />
                    More Info
                  </Link>
                </Button>
                <Button
                  asChild
                  className="flex-1 lg:flex-none lg:w-32 bg-primary hover:bg-primary/90 text-primary-foreground gap-2"
                >
                  <Link href={`/leagues/${league.id}/signup`}>
                    <UserPlus className="h-4 w-4" />
                    Sign Up
                  </Link>
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => setShareOpen(true)}
                  className="flex-1 lg:flex-none lg:w-32 text-muted-foreground hover:text-foreground gap-2"
                >
                  <Share2 className="h-4 w-4" />
                  Invite
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <ShareModal
        open={shareOpen}
        onOpenChange={setShareOpen}
        league={league}
      />
    </>
  )
}
